import {
  confirmResetPassword,
  confirmSignUp,
  confirmSignIn,
  fetchAuthSession,
  getCurrentUser,
  resendSignUpCode,
  resetPassword,
  signIn,
  signOut,
} from 'aws-amplify/auth';
import {
  OrganisationBlockedError,
  getOrganisationBlockedStatus,
} from '../lib/authErrors';
import { getStoredActiveRole, mapGroupToRole, mapGroupsToRoles } from '../lib/roles';
import { AuthUser, SignInResult, SignUpInput, SignUpResult } from '../types/auth';
import { authAPI } from './api';

export { OrganisationBlockedError } from '../lib/authErrors';

function normalizeEmail(email: string) {
  return email.trim().toLowerCase();
}

/** Persist last_login on the server after a real Cognito sign-in (not on session refresh). */
async function recordLoginOnServer(): Promise<void> {
  try {
    await authAPI.recordLogin();
  } catch {
    // Non-blocking; profile load should still succeed.
  }
}

async function resolveCurrentUser(): Promise<AuthUser | null> {
  try {
    const user = await getCurrentUser();
    const session = await fetchAuthSession();
    const email = session.tokens?.idToken?.payload?.email;
    const groupsClaim = session.tokens?.accessToken?.payload?.['cognito:groups'];
    const groups = Array.isArray(groupsClaim) ? groupsClaim.filter((value): value is string => typeof value === 'string') : [];
    const availableRoles = mapGroupsToRoles(groups);
    const activeRole = getStoredActiveRole(availableRoles) ?? (availableRoles.length === 1 ? availableRoles[0] : null);

    const authUser: AuthUser = {
      userId: user.userId,
      username: user.username,
      email: typeof email === 'string' ? email : user.signInDetails?.loginId ?? user.username,
      groups,
      role: activeRole ?? mapGroupToRole(groups),
      availableRoles,
      activeRole,
    };

    try {
      const profile = await authAPI.me();
      const data = profile.data ?? {};
      return {
        ...authUser,
        fullName: data.fullName,
        orgName: data.orgName ?? null,
        organisationId: data.organisationId ?? null,
        dateOfBirth: data.dateOfBirth,
        designation: data.designation,
        department: data.department,
        phoneNumber: data.phoneNumber,
        profileComplete: Boolean(data.profileComplete),
        departmentOptions: Array.isArray(data.departmentOptions)
          ? data.departmentOptions.map((x: unknown) => String(x).trim()).filter(Boolean)
          : undefined,
        employeeHelpMarkdown: data.employeeHelpMarkdown ?? null,
        orgLogoUrl: data.orgLogoUrl ?? null,
        orgHelpModel:
          data.orgHelpModel === 'freeman' || data.orgHelpModel === 'both'
            ? data.orgHelpModel
            : 'engage',
      };
    } catch (profileError) {
      const blockedStatus = getOrganisationBlockedStatus(profileError);
      if (blockedStatus) {
        try {
          await signOut();
        } catch {
          // ignore sign-out errors
        }
        throw new OrganisationBlockedError(blockedStatus);
      }
      throw profileError;
    }
  } catch (error) {
    if (error instanceof OrganisationBlockedError) {
      throw error;
    }
    return null;
  }
}

export async function getAuthenticatedUser() {
  return resolveCurrentUser();
}

export async function signUpWithEmail({ email, password }: SignUpInput): Promise<SignUpResult> {
  try {
    const result = await authAPI.signup({
      email: normalizeEmail(email),
      password,
    });

    return {
      isSignUpComplete: Boolean(result.data?.isSignUpComplete),
      nextStep: result.data?.nextStep ?? 'CONFIRM_SIGN_UP',
    };
  } catch (error: any) {
    const message = error?.response?.data?.error || error?.response?.data?.message || 'Sign up failed. Please try again.';
    throw new Error(Array.isArray(message) ? message.join(', ') : String(message));
  }
}

export async function confirmEmailSignUp(email: string, code: string) {
  return confirmSignUp({
    username: normalizeEmail(email),
    confirmationCode: code.trim(),
  });
}

export async function resendVerificationCode(email: string) {
  return resendSignUpCode({
    username: normalizeEmail(email),
  });
}

export async function signInWithEmail(email: string, password: string): Promise<SignInResult> {
  const result = await signIn({
    username: normalizeEmail(email),
    password,
  });

  const nextStep = result.nextStep.signInStep;
  if (nextStep === 'DONE') {
    const user = await resolveCurrentUser();
    await recordLoginOnServer();

    return {
      isSignedIn: result.isSignedIn,
      nextStep,
      user: user ?? undefined,
    };
  }

  return {
    isSignedIn: result.isSignedIn,
    nextStep,
  };
}

export async function completeNewPassword(newPassword: string): Promise<SignInResult> {
  const result = await confirmSignIn({
    challengeResponse: newPassword,
  });

  const nextStep = result.nextStep.signInStep;
  if (nextStep === 'DONE') {
    const user = await resolveCurrentUser();
    await recordLoginOnServer();

    return {
      isSignedIn: result.isSignedIn,
      nextStep,
      user: user ?? undefined,
    };
  }

  return {
    isSignedIn: result.isSignedIn,
    nextStep,
  };
}

export async function signOutUser() {
  await signOut();
}

export async function requestPasswordReset(email: string) {
  await resetPassword({ username: normalizeEmail(email) });
}

export async function confirmPasswordReset(email: string, code: string, newPassword: string) {
  await confirmResetPassword({
    username: normalizeEmail(email),
    confirmationCode: code.trim(),
    newPassword,
  });
}
